import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import './editable-title.scss';

interface EditTitleProps {
	defaultText?: string;
	onSubmit: (newText: string) => void;
	className?: string;
}

export default function EditableTitle({
	defaultText = '',
	onSubmit,
	className,
}: EditTitleProps): JSX.Element {
	const [isEditing, setIsEditing] = useState(false);
	const [text, setText] = useState(defaultText);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleClose = useCallback(
		(event: any) => {
			for (const element of event.path) {
				if (inputRef.current === element) { return; }
			}
			setIsEditing(false);
			setText(defaultText);
		},
		[setText, defaultText]
	);

	useEffect(() => {
		inputRef.current?.focus();
		if (isEditing) {
			window.addEventListener('click', handleClose);
		}
		return () => window.removeEventListener('click', handleClose);
	}, [isEditing, handleClose]);

	const handleKeyPress = ({ key }: KeyboardEvent) => {
		switch (key) {
			case 'Escape':
				setIsEditing(false);
				setText(defaultText);
				break;
			case 'Enter':
				setIsEditing(false);
				if (text === defaultText) { return; }
				onSubmit(text);
				break;
			default:
				return;
		}
	};
	const handleDoubleClick = () => {
		setIsEditing(true);
	};

	return (
		<div
			className={classNames(className)}
			onDoubleClick={handleDoubleClick}
			onKeyUp={handleKeyPress}
		>
			{isEditing ? (
				<input
					className="text-input"
					type="text"
					ref={inputRef}
					value={text}
					onChange={({ target }) => setText(target.value)}
				/>
			) : (
				<p>{defaultText}</p>
			)}
		</div>
	);
}
